import { customerFacingKeyForFailureCode } from './sync-failure-code-catalogue'

// Minimal translator shape: BOTH react-i18next's branded `TFunction` (used by
// SourcesTable) and the app's custom `useTranslation` hook `t` (used by
// SourceErrorModal, whose type lacks the i18next `$TFunctionBrand`) satisfy
// this single-key→string signature. Typing the param as the full i18next
// `TFunction` would reject the custom hook's `t`; this structural type accepts
// both call sites (same approach as useBalanceExport.ts).
type SyncErrorTranslator = (key: string) => string

const URL_PATTERN = /\bhttps?:\/\/\S+/gi
// Blockchain / JSON-RPC provider noise — endpoints, JSON-RPC codes, "full node", etc.
const BLOCKCHAIN_RPC_PATTERN = /\bRPC\b|json-?rpc|-3270\d|full node|node provider/i

/**
 * ⚠️⚠️ DOS FUENTES EN «ERROR», UN SOLO TEXTO — Y PARA UNA ERA MENTIRA.
 *
 * MEDIDO POR LA UI el 2026-08-24 en `/import`: abriendo «Detalles del error de
 * sincronización» de DOS fuentes blockchain distintas, la prosa era idéntica
 * palabra por palabra («…el nodo de red no pudo completar la solicitud.
 * Inténtalo de nuevo más tarde.»). Pero `bat.data_sources.last_error` —que para
 * una cartera guarda el error CRUDO, porque `recordImportFailureOnDataSource`
 * corre ANTES de que el processor lo mapee al catálogo seguro— tenía esto:
 *
 *   Polygon   «…RPC call failed after 3 retries: fetch failed»       → transitorio
 *   Ethereum  «RPC call failed after 3 retries: HTTP error 403: Forbidden»
 *                                                                    → RECHAZO
 *
 * `BLOCKCHAIN_RPC_PATTERN` casa las dos y las aplanaba en el mismo consejo. Para
 * un 403 «inténtalo más tarde» es una promesa que el tiempo no puede cumplir:
 * hace falta que un operador corrija la credencial del endpoint.
 *
 * 🔑 Se comprueba ANTES que `BLOCKCHAIN_RPC_PATTERN` porque el texto contiene
 * AMBAS firmas: el orden es lo único que decide cuál de las dos se cuenta.
 * Enumerar 401/403 y no «40x» es deliberado — un 429 es límite de peticiones y
 * ahí esperar SÍ es el remedio.
 */
const BLOCKCHAIN_ACCESS_DENIED_PATTERN =
  /\bHTTP error 40[13]\b|\bunauthorized\b|\bforbidden\b|\binvalid api key\b/i

/**
 * ⚠️⚠️ LA TERCERA NATURALEZA, Y LA ÚNICA QUE ES CULPA NUESTRA.
 *
 * MEDIDO en producción el 2026-09-04: una importación de cartera murió con «RPC
 * call failed: HTTP error 429: Too Many Requests». La causa no estaba en el
 * proveedor: el cliente RPC aplicaba a TODOS los endpoints de la cadena el
 * límite de tasa del PRIMERO (30 peticiones/segundo contra un nodo de 10). El
 * texto casa con `BLOCKCHAIN_RPC_PATTERN`, así que caía en «el nodo de red no
 * pudo completar la solicitud», que le cuelga a un tercero un exceso NUESTRO.
 *
 * 🔑 Se comprueba DESPUÉS del rechazo (un 401/403 nunca es exceso de ritmo) y
 * ANTES del genérico, por el mismo motivo de orden que aquél.
 *
 * Devuelve una clave de `common:monitoring.failureReasons.*` y no una de
 * `imports:sources.errorModal.*`: la frase es la MISMA que lee la pantalla de
 * admin, y una segunda copia se desincronizaría — el mismo criterio que ya
 * documenta `LOCALIZED_KEY_BY_STORED_MESSAGE` unas líneas más abajo.
 */
const BLOCKCHAIN_RATE_LIMITED_PATTERN =
  /\bHTTP error 429\b|\btoo many requests\b|\brate limit(?:ed|ing)?\b/i

/** La ÚNICA frase traducida del exceso de ritmo; se nombra una vez y se usa en
 *  los dos reconocimientos —por prosa cruda y por prosa del catálogo— para que
 *  no puedan divergir. */
const LOCALIZED_KEY_RATE_LIMITED = 'common:monitoring.failureReasons.blockchainRateLimited'

/** Compact surfaces — a table row, a progress line — where a long stack would break the layout. */
const COMPACT_MAX_LENGTH = 240

/**
 * Detail surfaces, where the message IS the content. Still bounded so a genuine
 * stack trace cannot blow the panel up, but far above any real diagnostic: the
 * header-mismatch message runs ~300 characters with both column lists.
 */
const DETAIL_MAX_LENGTH = 2000

/**
 * ⚠️⚠️ EL MOTIVO QUE EL CLIENTE LEE ESTABA FIJO EN CASTELLANO.
 *
 * `data_sources.last_error` guarda PROSA, y para las importaciones esa prosa
 * sale de `SAFE_IMPORT_FAILURE_MESSAGES` (backend), que es castellano fijo.
 *
 * MEDIDO POR LA UI el 2026-08-18: cambiando el idioma del CLIENTE a inglés con
 * su propio selector (`lang="en"`), la tabla de `/import` seguía diciendo
 *
 *     «El archivo que subiste ya no está disponible en el servidor, así que no
 *      se puede reimportar esta fuente. Vuelve a subir el archivo…»
 *
 * …a quien tiene que actuar. El #1724 arregló la pantalla del ADMIN porque allí
 * llega un `code`; aquí NO llega: la columna es `text` y no hay sitio para él
 * sin una migración, y producción va cuatro migraciones por detrás.
 *
 * 🔑 Así que se reconoce la PROSA contra el catálogo cerrado, que es lo que ya
 * hace `unknown-row-reason.ts` con los tokens del motor. Dos salvaguardas:
 *   - la comparación es EXACTA sobre el texto recortado, no por contención: un
 *     mensaje que sólo CONTENGA una de estas frases lleva más información y se
 *     muestra tal cual;
 *   - un test del backend pina estas cadenas contra el catálogo, así que
 *     cambiar la redacción allí pone el test en rojo y dice dónde mirar.
 *
 * Se reutilizan las claves de `common:monitoring.failureReasons.*` que ya
 * existen (#1724): las frases son las MISMAS y una segunda copia se
 * desincronizaría. El prefijo `monitoring` es el precio de no duplicarlas.
 */
/**
 * ⚠️⚠️ LA ÚNICA CAUSA MEDIDA EN LA QUE REINTENTAR ES IMPOSIBLE.
 *
 * Se saca a constante porque además de traducirse hay que RECONOCERLA: el
 * diálogo de error decide con ella si ofrecer «Reintentar sincronización» o la
 * puerta que de verdad recupera la fuente (ver `isMissingUploadedFileError`).
 */
export const STORED_MESSAGE_FILE_UNAVAILABLE =
  'El archivo que subiste ya no está disponible en el servidor, así que no se puede reimportar esta fuente. Vuelve a subir el archivo para importarlo de nuevo.'

/**
 * La ÚNICA frase traducida de esta causa. Se nombra una vez y se usa en los dos
 * reconocimientos —por prosa y por código— para que no puedan divergir: dos
 * redacciones del mismo hecho en dos paneles vecinos serían el defecto que este
 * módulo existe para cerrar.
 */
const LOCALIZED_KEY_FILE_UNAVAILABLE = 'common:monitoring.failureReasons.fileUnavailable'

const LOCALIZED_KEY_BY_STORED_MESSAGE: Readonly<Record<string, string>> = {
  'Un valor numérico de una fila está fuera del rango permitido':
    'common:monitoring.failureReasons.numericOutOfRange',
  'No se pudo procesar el archivo de importación':
    'common:monitoring.failureReasons.fileProcessing',
  'El archivo no coincide con el formato esperado por la plantilla de importación':
    'common:monitoring.failureReasons.noConfigurationMatched',
  'No se pudo determinar cómo leer el archivo: la importación no llegó con ninguna plantilla ni mapeo de columnas. Vuelve a lanzarla eligiendo el formato del archivo.':
    'common:monitoring.failureReasons.mappingsRequired',
  'La plantilla de este exchange está deshabilitada y no hay otra activa, así que no se pueden procesar sus movimientos. Contacta con soporte para que la reactiven: reintentar no servirá hasta entonces.':
    'common:monitoring.failureReasons.templateDisabled',
  [STORED_MESSAGE_FILE_UNAVAILABLE]: LOCALIZED_KEY_FILE_UNAVAILABLE,
  // La que graba la segadora en la FUENTE cuando cancela una sincronización
  // (`REAPED_SYNC_SOURCE_LAST_ERROR`, backend, 2026-09-27). Es la misma frase que
  // la tarjeta de «Importaciones recientes» saca del código `STUCK_JOB_REAPED`:
  // la celda de estado y la tarjeta ya no pueden contar dos causas distintas.
  'La importación dejó de avanzar y el sistema la canceló. Los movimientos ya importados se conservan: vuelve a lanzarla.':
    'common:syncWait.stalledAndCancelled',
  // En la práctica un fallo de cartera llega aquí como prosa RPC cruda y lo
  // atrapa antes `BLOCKCHAIN_RPC_PATTERN`; la entrada existe porque el catálogo
  // del backend ya puede guardar esta frase (`IMPORT_BLOCKCHAIN_UNAVAILABLE`,
  // 2026-08-23) y el pin del backend exige que la tabla sepa traducirla.
  'No se pudo conectar con la red blockchain para leer los movimientos de esta cartera. El fallo está en el proveedor de nodos, no en tus datos: vuelve a lanzar la sincronización más tarde. Los movimientos ya importados se conservan.':
    'common:monitoring.failureReasons.blockchainUnavailable',
  // Su gemela con la naturaleza OPUESTA (`IMPORT_BLOCKCHAIN_ACCESS_DENIED`,
  // 2026-08-24): el proveedor rechazó el acceso, y eso no se arregla esperando.
  // Igual que la de arriba, en la práctica el fallo de cartera llega como prosa
  // RPC cruda y lo atrapa antes `BLOCKCHAIN_ACCESS_DENIED_PATTERN`; la entrada
  // existe porque el pin del backend exige que la tabla sepa traducirla.
  'La conexión con la red blockchain llegó al proveedor de nodos, pero este rechazó el acceso, así que no se pudieron leer los movimientos de esta cartera. No es un problema de tus datos: hay que corregir la configuración de acceso en el servidor, y esperar no lo resuelve. Escríbenos desde «Soporte» y reintenta la sincronización cuando te confirmemos que está corregida. Los movimientos ya importados se conservan.':
    'common:monitoring.failureReasons.blockchainAccessDenied',
  // La TERCERA (`IMPORT_BLOCKCHAIN_RATE_LIMITED`, 2026-09-04): le pedimos al
  // nodo más de lo que admite. En la práctica el fallo de cartera llega como
  // prosa RPC cruda y lo atrapa antes `BLOCKCHAIN_RATE_LIMITED_PATTERN`; la
  // entrada existe porque el pin del backend exige que la tabla sepa traducirla.
  'Consultamos la red blockchain más deprisa de lo que este nodo admite y dejó de atendernos, así que la lectura de los movimientos de esta cartera quedó a medias. No es un problema de tus datos ni una caída del proveedor: el ritmo lo marcamos nosotros. Vuelve a lanzar la sincronización dentro de un rato y, si se repite, escríbenos desde «Soporte» para que ajustemos ese ritmo. Los movimientos ya importados se conservan.':
    LOCALIZED_KEY_RATE_LIMITED,
  // El respaldo del catálogo. El mensaje que el procesador graba de verdad
  // NOMBRA al medio («Este exchange (binance-futures) no admite…»), así que no
  // casa exacto y se muestra tal cual — que es lo correcto: lleva MÁS
  // información, y es la misma excepción documentada que
  // `IMPORT_NO_CONFIGURATION_MATCHED` con sus listas de columnas.
  'Este medio ya no admite la importación por CSV, así que esta importación no se ha ejecutado. Usa el importador personalizado para crear tu propia plantilla.':
    'common:monitoring.failureReasons.methodNotSupported',
  // BUG-123 (2026-09-27): ningún nodo activo puede leer la cartera entera y el
  // barrido se negó a leer a medias.
  'Ahora mismo ningún nodo de esta red blockchain puede leer tu cartera entera, así que no se ha importado nada para no dejarte un historial a medias. No es un problema de tus datos: es la configuración de nuestros nodos, y esperar no lo resuelve. Escríbenos desde «Soporte» y reintenta cuando te confirmemos que está corregida. Los movimientos ya importados se conservan.':
    'common:monitoring.failureReasons.blockchainNodeIncapable',
  'La importación falló': 'common:monitoring.failureReasons.generic',
}

/**
 * ¿La sincronización murió porque el archivo subido ya no está en el servidor?
 *
 * MEDIDO POR LA UI el 2026-08-23: cuando la respuesta es «sí», reintentar es
 * una acción cuyo único desenlace posible es el error que el usuario ya está
 * leyendo. `csv-sync.handler.ts` reencola el trabajo con la MISMA
 * `config.lastFilePath`, y no existe ninguna segunda copia del fichero
 * (`sweep-stale-uploads.ts` borra por antigüedad sin archivar). Los bytes no
 * están: el reintento es imposible, no improbable.
 *
 * Coincidencia EXACTA sobre el texto recortado, igual que
 * `LOCALIZED_KEY_BY_STORED_MESSAGE` — y aquí es además la dirección PRUDENTE:
 * ante cualquier duda se conserva el botón.
 *
 * ⚠️ MATIZ AÑADIDO EL 2026-08-24. Este comentario decía «para un fallo de red o
 * de proveedor de nodos reintentar es exactamente el remedio», y eso sólo vale
 * para la mitad transitoria. Cuando el proveedor RECHAZA el acceso (401/403),
 * reintentar ahora mismo da el mismo error — pero, a diferencia del archivo que
 * ya no existe, sigue siendo una acción CON desenlace posible: en cuanto un
 * operador corrija la credencial del endpoint desde la UI de admin,
 * `reloadBlockchainRegistries` vacía la caché de clientes RPC y el siguiente
 * reintento usa la configuración nueva. Por eso el botón se CONSERVA también ahí
 * y lo que se corrigió fue el TEXTO, que prometía que el tiempo lo arreglaba.
 */
export function isMissingUploadedFileError(rawError: string | null | undefined): boolean {
  if (rawError === null || rawError === undefined) return false
  return rawError.trim() === STORED_MESSAGE_FILE_UNAVAILABLE
}

/** Las frases que este módulo reconoce. Exportado para el guardián del backend. */
export const RECOGNIZED_STORED_FAILURE_MESSAGES: readonly string[] = Object.keys(
  LOCALIZED_KEY_BY_STORED_MESSAGE,
)

/**
 * ⚠️⚠️ LA MISMA CAUSA, GUARDADA EN OTRO SITIO Y EN OTRO IDIOMA.
 *
 * `LOCALIZED_KEY_BY_STORED_MESSAGE` reconoce la PROSA de `data_sources.last_error`,
 * que el backend escribe desde `SAFE_IMPORT_FAILURE_MESSAGES` y por eso está en
 * castellano. «Importaciones recientes» NO lee esa columna: lee
 * `platform_jobs.error_data`, donde el procesador guarda el error CRUDO —
 * `createApiError('FILE_NOT_FOUND', 'CSV file not found: …')`, en inglés— y la
 * prosa no casa con ninguna entrada del catálogo.
 *
 * MEDIDO en la base de datos e2e el 2026-08-24, sobre los tipos de trabajo que
 * este panel lista: las 17 filas «CSV file not found» son la misma causa que el
 * diálogo de la fuente ya traduce, y las 4 posteriores al 2026-08-18 traen
 * `error_data.code = 'FILE_NOT_FOUND'` (las 13 anteriores son de antes de que el
 * worker preservara el código anidado y siguen sin él, igual que documenta
 * `sync-failure-reason.ts`).
 *
 * 🔑 Dos códigos, una causa — exactamente como ya lo dice el catálogo por código
 * del monitor de sincronización: `FILE_NOT_FOUND` es el del `createApiError` que
 * viaja en `error_data.code`, `IMPORT_FILE_UNAVAILABLE` el del catálogo seguro
 * que el worker adjunta desde `metadata.failureCode`. Ambos apuntan a la MISMA
 * clave traducida, que es la que ya lee el diálogo de error de la fuente.
 */
export const MISSING_UPLOADED_FILE_CODES: readonly string[] = [
  'FILE_NOT_FOUND',
  'IMPORT_FILE_UNAVAILABLE',
]

/**
 * El texto —ya saneado— con el que la MISMA causa llega cuando el trabajo es
 * anterior a que el worker preservara el código anidado (2026-08-18).
 *
 * `extractSafeJobErrorMessage` corta el error crudo en la primera marca insegura
 * —la ruta del servidor— así que
 *
 *     CSV file not found: /…/temp/uploads/<tenant>/<usuario>/<fichero>.csv.
 *     The file may have been deleted or moved.
 *
 * queda EXACTAMENTE en «CSV file not found». MEDIDO: 13 filas así en la base de
 * datos e2e, todas del mismo generador, ninguna con código.
 *
 * ⚠️ NO es una segunda traducción: apunta a la clave que ya existe. Y sigue
 * siendo coincidencia EXACTA — un mensaje que sólo la CONTENGA lleva más
 * información (por ejemplo, si el saneado dejara de recortar) y conserva su
 * botón. La variante hermana del procesador («…or became inaccessible during
 * processing») no aparece en lo medido y no se añade a ojo: llega con código.
 */
const STORED_MESSAGE_FILE_NOT_FOUND_SANITIZED = 'CSV file not found'

/** Lo que el backend guardó de un trabajo fallido: su texto y su código. */
export interface StoredJobFailure {
  readonly message: string | null | undefined
  readonly code: string | null | undefined
  /**
   * Sólo para una siega (`STUCK_JOB_REAPED`): cuál de las dos preguntas de la
   * segadora falló (#2205). Distingue «el proceso se detuvo» de «siguió vivo sin
   * avanzar», que no son la misma noticia ni prescriben el mismo movimiento.
   */
  readonly reapReason?: string | null
}

/**
 * ¿Este trabajo murió porque el archivo subido ya no está en el servidor?
 *
 * Misma pregunta que `isMissingUploadedFileError` y con el MISMO criterio —
 * coincidencia EXACTA, nunca por contención— sobre las tres formas en que la
 * causa llega: el código, la prosa castellana del catálogo seguro y el texto
 * inglés ya saneado del procesador. La dirección sigue siendo la prudente: ante
 * la duda se responde `false` y el reintento se CONSERVA.
 */
export function isMissingUploadedFileFailure(failure: StoredJobFailure): boolean {
  const { code, message } = failure
  if (typeof code === 'string' && MISSING_UPLOADED_FILE_CODES.includes(code.trim())) return true
  if (typeof message === 'string' && message.trim() === STORED_MESSAGE_FILE_NOT_FOUND_SANITIZED) {
    return true
  }
  return isMissingUploadedFileError(message)
}

/** Shared cleanup: swap RPC noise for a localized line, strip URLs, collapse whitespace. */
/** La naturaleza de un error RPC CRUDO, o `null` si no es un error RPC. */
export type RawRpcFailureKind = 'access_denied' | 'rate_limited' | 'unavailable'

/**
 * El MISMO reconocimiento que usa el panel del cliente, exportado para que el
 * superadmin clasifique igual las filas antiguas sin código (BUG-127). El orden
 * es el de `cleanSyncError`: rechazo, luego exceso de ritmo, luego el genérico.
 */
export function classifyRawRpcFailure(
  rawError: string | null | undefined,
): RawRpcFailureKind | null {
  if (rawError === null || rawError === undefined || !BLOCKCHAIN_RPC_PATTERN.test(rawError)) {
    return null
  }
  if (BLOCKCHAIN_ACCESS_DENIED_PATTERN.test(rawError)) return 'access_denied'
  if (BLOCKCHAIN_RATE_LIMITED_PATTERN.test(rawError)) return 'rate_limited'
  return 'unavailable'
}

function cleanSyncError(
  rawError: string | null | undefined,
  t: SyncErrorTranslator,
): { text: string; final: boolean } {
  if (rawError === null || rawError === undefined || rawError.trim().length === 0) {
    return { text: t('imports:sources.errorModal.noErrorMessage'), final: true }
  }
  // El rechazo de acceso va PRIMERO: su texto crudo casa también con
  // `BLOCKCHAIN_RPC_PATTERN`, así que dejarlo detrás lo devolvería al consejo
  // que no se puede cumplir. Ver el bloque de arriba.
  // El exceso de ritmo va entre medias: sin su rama volvería a contarse como «el
  // nodo no pudo completar la solicitud», que atribuye a un tercero algo que
  // causamos nosotros. El orden vive en `classifyRawRpcFailure`.
  const rpcKind = classifyRawRpcFailure(rawError)
  if (rpcKind === 'access_denied') {
    return { text: t('imports:sources.errorModal.blockchainAccessDenied'), final: true }
  }
  if (rpcKind === 'rate_limited') {
    return { text: t(LOCALIZED_KEY_RATE_LIMITED), final: true }
  }
  if (rpcKind === 'unavailable') {
    return { text: t('imports:sources.errorModal.blockchainSyncFailed'), final: true }
  }
  // Coincidencia EXACTA con el catálogo de mensajes de importación: ese texto no
  // lleva nada que la traducción pierda, así que se sustituye entero.
  const clave = LOCALIZED_KEY_BY_STORED_MESSAGE[rawError.trim()]
  if (clave !== undefined) {
    return { text: t(clave), final: true }
  }
  return { text: rawError.replace(URL_PATTERN, '').replace(/\s+/g, ' ').trim(), final: false }
}

function capAt(text: string, max: number): string {
  return text.length > max ? `${text.slice(0, max - 1)}…` : text
}

/**
 * Turn a raw backend sync error into a clean, localized string for a COMPACT
 * surface. Blockchain/RPC provider failures are replaced wholesale with a
 * localized message (their raw content is provider noise + sometimes vendor
 * URLs, useless to the user); any other message has URLs stripped and is
 * truncated so a long stack can't break the layout. `t` is the
 * imports-namespace-bound translator.
 */
export function formatSyncErrorForDisplay(
  rawError: string | null | undefined,
  t: SyncErrorTranslator,
): string {
  const { text, final } = cleanSyncError(rawError, t)
  return final ? text : capAt(text, COMPACT_MAX_LENGTH)
}

/**
 * Same cleanup for a surface the user OPENED to read the error — the history
 * row's expanded detail, the source error modal.
 *
 * ⚠️ Medido en el navegador: el fallo de cabeceras se corta a 240 caracteres
 * JUSTO dentro de la segunda lista —
 *
 *     …Columnas esperadas: Fecha, Tipo, Cantidad origen, … Moneda comision….
 *     Columnas encontradas: Fecha, Tip…
 *
 * — y ese mensaje existe SOLO para comparar las dos listas. Con la mitad
 * encontrada cortada al décimo carácter no se puede diagnosticar nada, y el
 * panel de detalle es precisamente donde el usuario fue a leerlo entero.
 */
export function formatSyncErrorForDetail(
  rawError: string | null | undefined,
  t: SyncErrorTranslator,
): string {
  const { text, final } = cleanSyncError(rawError, t)
  return final ? text : capAt(text, DETAIL_MAX_LENGTH)
}

/**
 * Igual que `formatSyncErrorForDisplay`, pero para una superficie que además
 * tiene el CÓDIGO del fallo (`platform_jobs.error_data.code`).
 *
 * ⚠️ El código se consulta SÓLO para la causa del archivo ausente, no como
 * catálogo general. Un fallo con código puede traer un mensaje MÁS informativo
 * que su traducción genérica —el de cabeceras compara las dos listas de
 * columnas, y `formatSyncErrorForDetail` existe precisamente para no cortarlo—,
 * así que sustituir todo mensaje que traiga código cambiaría un defecto por
 * otro. Aquí sólo se traduce aquello cuyo texto almacenado no dice nada más:
 * medido, «CSV file not found» a secas.
 */
export function formatJobFailureForDisplay(
  failure: StoredJobFailure,
  t: SyncErrorTranslator,
): string {
  return formatJobFailure(failure, t, formatSyncErrorForDisplay)
}

/** La misma lectura por código para la superficie de detalle. */
export function formatJobFailureForDetail(
  failure: StoredJobFailure,
  t: SyncErrorTranslator,
): string {
  return formatJobFailure(failure, t, formatSyncErrorForDetail)
}

/**
 * Lectura por código compartida por las dos superficies: el código con causa
 * conocida manda, luego el archivo ausente, y sólo entonces el texto del
 * mensaje, formateado por la superficie (`formatMessage`).
 */
function formatJobFailure(
  failure: StoredJobFailure,
  t: SyncErrorTranslator,
  formatMessage: (message: string | null | undefined, t: SyncErrorTranslator) => string,
): string {
  const porCodigo = customerFacingKeyForFailureCode(
    failure.code,
    failure.message,
    failure.reapReason,
  )
  if (porCodigo !== undefined) return t(porCodigo)
  if (isMissingUploadedFileFailure(failure)) return t(LOCALIZED_KEY_FILE_UNAVAILABLE)
  return formatMessage(failure.message, t)
}
